import React, { useState, useEffect } from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  TextField,
  Button,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Chip
} from "@mui/material";
import {
  Close as CloseIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  VerifiedUser as VerifiedUserIcon,
  Info as InfoIcon,
  Assignment as AssignmentIcon
} from "@mui/icons-material";
import useDrawerResize from "./useDrawerResize";
import DrawerResizeHandle from "./DrawerResizeHandle";
import { createRole, updateRole, getRoleDetail } from "../api/coordinatorApi";
import { notification } from "../utils/messages";

const DRAWER_WIDTH = 560;
const DRAWER_MIN_WIDTH = 420;
const DRAWER_MAX_WIDTH = 1080;

export default function RoleFormDrawer({ open, onClose, onSuccess, editMode, roleData, postTypes }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    post_type_id: "",
    sort_order: "",
    name: "",
    role_overview: "",
    footer_note: "",
    responsibilities: [{ title: "", description: "", sort_order: 1 }],
    forceReAccept: false
  });
  const [errors, setErrors] = useState({});
  const [originalPostTypeId, setOriginalPostTypeId] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingPayload, setPendingPayload] = useState(null);

  const { width: drawerWidth, onDragStart: onResizeStart } = useDrawerResize(
    "roleForm",
    DRAWER_WIDTH,
    DRAWER_MIN_WIDTH,
    DRAWER_MAX_WIDTH
  );

  useEffect(() => {
    if (open) {
      if (editMode && roleData) {
        loadRoleDetail(roleData.id);
      } else {
        resetForm();
      }
    }
  }, [open, editMode, roleData]);

  const loadRoleDetail = async (id) => {
    setLoading(true);
    try {
      const response = await getRoleDetail(id);
      if (response.status === 1) {
        const detail = response.output;
        setOriginalPostTypeId(detail.post_type_id);
        setFormData({
          post_type_id: detail.post_type_id || "",
          sort_order: detail.sort_order || "",
          name: detail.name || "",
          role_overview: detail.role_overview || "",
          footer_note: detail.footer_note || "",
          responsibilities:
            detail.responsibilities?.length > 0
              ? detail.responsibilities.map((r) => ({
                  id: r.id,
                  title: r.title || "",
                  description: r.description || "",
                  sort_order: r.sort_order
                }))
              : [{ title: "", description: "", sort_order: 1 }]
        });
      }
    } catch (error) {
      notification({ type: "error", message: "Failed to load role details" })();
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      post_type_id: "",
      sort_order: "",
      name: "",
      role_overview: "",
      footer_note: "",
      responsibilities: [{ title: "", description: "", sort_order: 1 }],
      forceReAccept: false
    });
    setErrors({});
    setOriginalPostTypeId(null);
    setConfirmOpen(false);
    setPendingPayload(null);
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleResponsibilityChange = (index, field, value) => {
    const updated = [...formData.responsibilities];
    updated[index] = { ...updated[index], [field]: value };
    setFormData((prev) => ({ ...prev, responsibilities: updated }));
  };

  const addResponsibility = () => {
    setFormData((prev) => ({
      ...prev,
      responsibilities: [
        ...prev.responsibilities,
        { title: "", description: "", sort_order: prev.responsibilities.length + 1 }
      ]
    }));
  };

  const removeResponsibility = (index) => {
    if (formData.responsibilities.length <= 1) return;
    const updated = formData.responsibilities.filter((_, i) => i !== index);
    updated.forEach((r, i) => (r.sort_order = i + 1));
    setFormData((prev) => ({ ...prev, responsibilities: updated }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.post_type_id) newErrors.post_type_id = "Post type is required";
    if (!formData.sort_order || Number(formData.sort_order) < 1) newErrors.sort_order = "Position must be at least 1";
    if (!formData.name.trim()) newErrors.name = "Role name is required";
    if (!formData.role_overview.trim()) newErrors.role_overview = "Role overview is required";
    if (!formData.responsibilities.some((r) => r.title.trim() || r.description.trim())) {
      newErrors.responsibilities = "At least one responsibility is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const buildPayload = () => {
    const loginUser = JSON.parse(localStorage.getItem("userData"));
    return {
      post_type_id: formData.post_type_id,
      sort_order: Number(formData.sort_order),
      name: formData.name,
      role_overview: formData.role_overview,
      footer_note: formData.footer_note,
      force_re_accept: formData.forceReAccept,
      updated_by: loginUser?.u_id || null,
      responsibilities: formData.responsibilities
        .filter((r) => r.title.trim() || r.description.trim())
        .map((r, i) => ({
          ...(r.id && { id: r.id }),
          title: r.title,
          description: r.description,
          sort_order: i + 1
        }))
    };
  };

  const doSave = async (payload) => {
    setSaving(true);
    try {
      let response;
      if (editMode && roleData) {
        response = await updateRole({ id: roleData.id, ...payload });
      } else {
        response = await createRole(payload);
      }

      if (response.status === 1) {
        if (editMode && response.output?.force_re_accept) {
          notification({
            type: "info",
            message: "All coordinators for this role must re-accept responsibilities."
          })();
        } else if (editMode && response.output?.post_type_changed) {
          notification({
            type: "info",
            message: `Post type changed. ${response.output.coordinators_synced} coordinator(s) moved to new post type.`
          })();
        } else {
          notification({
            type: "success",
            message: response.message || (editMode ? "Role updated" : "Role created")
          })();
        }
        onSuccess();
      } else {
        notification({ type: "error", message: response.message || "Operation failed" })();
      }
    } catch (error) {
      notification({ type: "error", message: "An error occurred" })();
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    const payload = buildPayload();

    if (editMode && originalPostTypeId != null && Number(formData.post_type_id) !== Number(originalPostTypeId)) {
      setPendingPayload(payload);
      setConfirmOpen(true);
      return;
    }

    await doSave(payload);
  };

  const handleConfirmSave = async () => {
    setConfirmOpen(false);
    if (pendingPayload) {
      await doSave(pendingPayload);
      setPendingPayload(null);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: drawerWidth,
          bgcolor: "background.paper",
          boxShadow: "-10px 0 30px rgba(0, 0, 0, 0.05)"
        }
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%", position: "relative" }}>
        <DrawerResizeHandle onMouseDown={onResizeStart} />
        {/* Header */}
        <Box
          sx={{
            px: 3,
            py: 2,
            borderBottom: "1px solid",
            borderColor: "divider",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between"
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box sx={{ p: 1, borderRadius: 1, bgcolor: "secondary.light", color: "secondary.main", display: "flex" }}>
              <VerifiedUserIcon />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, color: "text.primary" }}>
                {editMode ? "Edit Role" : "Create New Role"}
              </Typography>
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                Configure access levels and responsibilities
              </Typography>
            </Box>
          </Stack>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Content */}
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", flex: 1 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ flex: 1, overflowY: "auto", px: 3, py: 4 }}>
            {/* Basic Information */}
            <Box sx={{ mb: 4 }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <InfoIcon sx={{ color: "primary.main", fontSize: 20 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>
                  Basic Information
                </Typography>
              </Stack>
              <Stack spacing={2.5}>
                <FormControl fullWidth size="small" error={!!errors.post_type_id}>
                  <InputLabel>Post Type *</InputLabel>
                  <Select
                    value={formData.post_type_id}
                    onChange={(e) => handleChange("post_type_id", e.target.value)}
                    label="Post Type *"
                    input={<OutlinedInput label="Post Type *" />}
                    disabled={false}
                  >
                    <MenuItem value="">
                      <em>Select post type...</em>
                    </MenuItem>
                    {postTypes.map((pt) => (
                      <MenuItem key={pt.id} value={pt.id}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <span>{pt.name}</span>
                          {pt.is_self === 1 && (
                            <Chip
                              label="Self"
                              size="small"
                              sx={{
                                height: 20,
                                fontSize: "0.65rem",
                                fontWeight: 700,
                                bgcolor: "info.lighter",
                                color: "info.dark"
                              }}
                            />
                          )}
                        </Stack>
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.post_type_id && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                      {errors.post_type_id}
                    </Typography>
                  )}
                </FormControl>

                <TextField
                  fullWidth
                  size="small"
                  label="Position *"
                  type="number"
                  value={formData.sort_order}
                  onChange={(e) => handleChange("sort_order", e.target.value)}
                  placeholder="e.g. 1"
                  inputProps={{ min: 1 }}
                  error={!!errors.sort_order}
                  helperText={errors.sort_order || "Unique position within this post type"}
                />

                <Box>
                  <TextField
                    fullWidth
                    size="small"
                    label="Role Name *"
                    value={formData.name}
                    onChange={(e) => handleChange("name", e.target.value)}
                    placeholder="e.g. Senior Security Auditor"
                    error={!!errors.name}
                    helperText={errors.name}
                  />
                </Box>

                <Box>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="Role Overview *"
                    value={formData.role_overview}
                    onChange={(e) => handleChange("role_overview", e.target.value)}
                    placeholder="Briefly describe the primary purpose of this role..."
                    error={!!errors.role_overview}
                    helperText={errors.role_overview}
                    sx={{ "& .MuiInputBase-root": { resize: "none" } }}
                  />
                </Box>
              </Stack>
            </Box>

            {/* Key Responsibilities */}
            <Box sx={{ mb: 3 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <AssignmentIcon sx={{ color: "primary.main", fontSize: 20 }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>
                    Key Responsibilities
                  </Typography>
                </Stack>
              </Stack>

              {errors.responsibilities && (
                <Typography variant="caption" color="error" sx={{ display: "block", mb: 1 }}>
                  {errors.responsibilities}
                </Typography>
              )}

              <Stack spacing={2}>
                {formData.responsibilities.map((resp, index) => (
                  <Box
                    key={index}
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      border: "1px solid",
                      borderColor: "divider",
                      bgcolor: "grey.50",
                      position: "relative",
                      "&:hover": { bgcolor: "grey.100" }
                    }}
                  >
                    <Stack spacing={1.5}>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Title of responsibility"
                        value={resp.title}
                        onChange={(e) => handleResponsibilityChange(index, "title", e.target.value)}
                        sx={{ "& .MuiInputBase-root": { fontWeight: 600 } }}
                      />
                      <TextField
                        fullWidth
                        multiline
                        rows={2}
                        placeholder="Description of duties..."
                        value={resp.description}
                        onChange={(e) => handleResponsibilityChange(index, "description", e.target.value)}
                        sx={{ "& .MuiInputBase-root": { fontSize: "0.875rem", resize: "none" } }}
                      />
                    </Stack>
                    {formData.responsibilities.length > 1 && (
                      <IconButton
                        size="small"
                        onClick={() => removeResponsibility(index)}
                        sx={{
                          position: "absolute",
                          top: -8,
                          right: -8,
                          bgcolor: "error.light",
                          color: "error.main",
                          "&:hover": { bgcolor: "error.main", color: "white" },
                          opacity: { xs: 1, md: 0 },
                          ".MuiBox-root:hover &": { opacity: 1 }
                        }}
                      >
                        <DeleteIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    )}
                  </Box>
                ))}
              </Stack>

              <Button
                fullWidth
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={addResponsibility}
                sx={{
                  mt: 2,
                  py: 1.5,
                  borderStyle: "dashed",
                  borderColor: "grey.300",
                  color: "text.secondary",
                  "&:hover": { borderColor: "secondary.main", color: "secondary.main", bgcolor: "secondary.lighter" }
                }}
              >
                Add Responsibility
              </Button>
            </Box>

            {/* Footer Note */}
            <Box sx={{ mb: 3 }}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Footer Note (Optional)"
                value={formData.footer_note}
                onChange={(e) => handleChange("footer_note", e.target.value)}
                placeholder="This role plays a vital part in..."
                sx={{ "& .MuiInputBase-root": { resize: "none" } }}
              />
            </Box>

            {/* Force Re-Accept — Edit Mode Only */}
            {editMode && (
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  bgcolor: "grey.50"
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.forceReAccept}
                      onChange={(e) => handleChange("forceReAccept", e.target.checked)}
                      size="small"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Force coordinators to re-accept
                      </Typography>
                      <Typography variant="caption" sx={{ color: "text.secondary" }}>
                        All coordinators assigned to this role will need to review and accept the updated responsibilities.
                      </Typography>
                    </Box>
                  }
                  sx={{ m: 0, alignItems: "flex-start" }}
                />
              </Box>
            )}
          </Box>
        )}

        {/* Footer */}
        <Box
          sx={{
            px: 3,
            py: 2,
            borderTop: "1px solid",
            borderColor: "divider",
            display: "flex",
            gap: 2
          }}
        >
          <Button
            fullWidth
            variant="outlined"
            onClick={onClose}
            disabled={saving}
            sx={{ py: 1.25, fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            fullWidth
            variant="contained"
            onClick={handleSubmit}
            disabled={saving}
            sx={{ py: 1.25, fontWeight: 600 }}
          >
            {saving ? <CircularProgress size={20} /> : "Save Role"}
          </Button>
        </Box>
      </Box>

      {/* Post Type Change Confirmation */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>Change Post Type?</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Changing the post type will move all <strong>Chapter coordinators</strong> assigned to this role under the new post type. Continue?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmOpen(false)} variant="outlined" sx={{ fontWeight: 600 }}>
            Cancel
          </Button>
          <Button onClick={handleConfirmSave} variant="contained" color="error" sx={{ fontWeight: 600 }}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Drawer>
  );
}
